import { useAtomValue } from 'jotai'
import { useCallback, useState } from 'react'
import { useLocation, useNavigate } from 'react-router'

import { isAuthenticatedAtom } from '../../domains/auth/AtomAuth'
import { currentUserAtom } from '../../domains/user/AtomUsers'
import PrivateSessionService from '../../services/PrivateSessionService'
import useBar from '../common/Bar'

export const usePrivateSessionBooking = () => {
  const user = useAtomValue(currentUserAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)

  const navigate = useNavigate()
  const location = useLocation()
  const bar = useBar()

  // 预订相关状态
  const [bookingNotes, setBookingNotes] = useState('')
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false)
  const [error, setError] = useState(null)
  const [isBooking, setIsBooking] = useState(false)

  // 处理预订按钮点击
  const handleOpenBookingDialog = useCallback(
    (instructor, date, timeSlots) => {
      // 检查用户是否已登录
      if (!isAuthenticated) {
        navigate('/login', {
          state: {
            message: 'Please sign in to book a session',
            redirectTo: location.pathname,
          },
        })
        return
      }

      if (!instructor?.resourceId || !date || timeSlots.size === 0) {
        setError('Please select instructor, date and at least one time slot')
        return
      }

      setOpenConfirmDialog(true)
    },
    [isAuthenticated, location, navigate],
  )

  // 处理确认预订
  const handleConfirmBooking = useCallback(
    async (selectedSessions) => {
      if (selectedSessions.length === 0) {
        bar.error('Please select at least one session')
        return
      }

      // 检查用户是否已登录
      if (!isAuthenticated) {
        navigate('/login', {
          state: {
            message: 'Please sign in to book a session',
            redirectTo: location.pathname,
          },
        })
        return
      }

      setIsBooking(true)

      try {
        // 批量预订所有选中的课程
        await Promise.all(
          selectedSessions.map((session) =>
            PrivateSessionService.registerSession(
              session.resourceId,
              [user.resourceId], // participantIds
              [], // participants
              bookingNotes, // notes
            ),
          ),
        )

        bar.success('Successfully booked all sessions!')
        // 等待短暂时间显示成功消息
        setTimeout(() => {
          navigate('/mysessions', {
            state: {
              bookingSuccess: true,
              sessionIds: selectedSessions.map((s) => s.resourceId),
            },
          })
        }, 1000)
      } catch (error) {
        console.error('Booking failed:', error)
        bar.error(error.message || 'Failed to book sessions')
      } finally {
        setIsBooking(false)
        setOpenConfirmDialog(false)
        setBookingNotes('')
      }
    },
    [user, bookingNotes, isAuthenticated, location, navigate, bar],
  )

  return {
    isBooking,
    error,
    bookingNotes,
    setBookingNotes,

    openConfirmDialog,
    setOpenConfirmDialog,

    handleOpenBookingDialog,
    handleConfirmBooking,
  }
}

export default usePrivateSessionBooking
